import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { DeploymentPlayer } from 'cccisd-laravel-assignment';
import CccisdToggle from 'cccisd-toggle';
import { BulkUploader } from 'cccisd-laravel-nexus';
import _get from 'lodash/get';
import Axios from 'cccisd-axios';
import Loader from 'cccisd-loader';
import { getDeployments, getAssignmentProgressData, setBulkDefData, setImportData } from 'js/reducers/nasn.js';
import { useMetricspawnQuery } from 'js/queries';
import Header from '../../../components/DataEntryHeader/PublicSchools';
import style from './style.css';

const PrivateSchoolDataEntry = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [modalClosed, setModalClosed] = useState(0);

    const reduxStore = useSelector(state => state.app.nasn);
    const dispatch = useDispatch();

    const metricspawnQuery = useMetricspawnQuery();

    useEffect(() => {
        setIsLoading(true);
        (async () => {
            await dispatch(getDeployments('privateSchool'));
            setIsLoading(false);
        })();
    }, []);

    const deploymentHandle = reduxStore.selectedDeployment;
    const deploymentId = reduxStore.deploymentId;
    const Boilerplate = window.cccisd.boilerplate;

    useEffect(() => {
        // Extra check because we don't want this running with deploymentHandle's initial, falsy value
        if (deploymentHandle && !isLoading) {
            // DeploymentPlayer doesn't re-render when deploymentHandle is changed, so we setLoading here so entire page is forced to re-render.
            setIsLoading(true);
            (async () => {
                const stateDeploymentHandle = deploymentHandle.slice(0, 9) + '_statePrivate';
                dispatch(
                    getAssignmentProgressData({
                        groupType: 'privateSchool',
                        deploymentHandle,
                        stateDeploymentHandle,
                    })
                );

                setIsLoading(false);
            })();
        }
    }, [deploymentHandle, modalClosed, isLoading]);

    const handleImportDataToggle = async () => {
        const newImportToggleValue = !reduxStore.importData.privateSchool;
        if (newImportToggleValue === true) {
            // only save the group as a respondent if toggle is true
            await Axios.post(Boilerplate.route('api.assignmentDeploymentRespondent.store', { deploymentId }), {
                pawnIds: [metricspawnQuery.data.metricspawn.pawnId],
            });
        }

        await Axios.post(Boilerplate.route('api.assignmentProgress.update'), {
            deploymentId: reduxStore.deploymentId,
            pawnId: metricspawnQuery.data.metricspawn.pawnId,
            flowlist: 'default',
            additional_info: {
                privateSchoolImportData: !reduxStore.importData.privateSchool,
            },
        });
        dispatch(
            setImportData({
                privateSchool: !reduxStore.importData.privateSchool,
            })
        );
    };

    const getBulkUploadDef = async () => {
        if (!deploymentId) {
            setIsLoading(false);
            console.error('No deployment id; could not retrieve bulk upload definition');
            return;
        }
        if (!reduxStore.bulkDefData[deploymentId]) {
            const response = await Axios.get(Boilerplate.route('api.nexus.bulkupload.deploymentRespondentMetricsDef'), {
                params: { deploymentId },
            });
            const responseBulkDef = _get(response, 'data.result.original.data');
            if (responseBulkDef) {
                dispatch(setBulkDefData({ [deploymentId]: responseBulkDef }));
                return responseBulkDef;
            }
            console.error('Could not retrieve bulk upload definition');
        }
        setIsLoading(false);
        return {};
    };

    const render = () => {
        const bulkDef = reduxStore.bulkDefData[deploymentId];
        if (reduxStore.deploymentIsOpen === false) {
            return (
                <>
                    <Header deploymentHandle={deploymentHandle} options={reduxStore.deployments} />
                    <div className={style.notification}>Data entry for this school year is now closed.</div>
                </>
            );
        }
        if (reduxStore.stateEntersPrivateData) {
            return (
                <>
                    <Header deploymentHandle={deploymentHandle} options={reduxStore.deployments} />
                    <div className={style.notification}>
                        Data entry is being managed by your state this school year. If you have any questions, please
                        contact your state administrator.
                    </div>
                </>
            );
        }
        return (
            <>
                <Header deploymentHandle={deploymentHandle} options={reduxStore.deployments} />
                <div
                    style={{
                        display: 'grid',
                        gridRowGap: '0.5em',
                        marginBottom: '1em',
                    }}
                >
                    <CccisdToggle
                        label="Will you import data this year?"
                        value={reduxStore.importData.privateSchool}
                        onChange={() => handleImportDataToggle()}
                    />
                </div>
                {reduxStore.importData.privateSchool ? (
                    <>
                        <div className="alert alert-info" role="alert">
                            Please reach out to NASN staff at{' '}
                            <a href="mailto:data@nasn.org" className="alert-link">
                                data@nasn.org
                            </a>{' '}
                            for technical assistance before your first upload.
                        </div>
                        <>
                            <BulkUploader
                                handle="deploymentRespondentMetrics"
                                trigger={
                                    <button type="button" className="btn btn-primary">
                                        Bulk Import School Data
                                    </button>
                                }
                                definition={bulkDef ? bulkDef : undefined}
                                loadDefinition={getBulkUploadDef}
                                deploymentId={deploymentId}
                                afterClose={() => setModalClosed(prevVal => prevVal + 1)}
                                modalTitle="Bulk Import School Data"
                            />
                            {reduxStore.importedInfo.imported && (
                                <div
                                    style={{
                                        fontStyle: 'italic',
                                    }}
                                >
                                    {' '}
                                    Last imported on{' '}
                                    {reduxStore.importedInfo.importedDate &&
                                        reduxStore.importedInfo.importedDate.slice(0, 10)}
                                </div>
                            )}
                        </>
                    </>
                ) : (
                    <>
                        <DeploymentPlayer
                            deploymentHandle={deploymentHandle}
                            pawnId={metricspawnQuery.data.metricspawn.pawnId}
                            pawnHash={metricspawnQuery.data.metricspawn.pawnHash}
                            onComplete={() => {
                                setIsLoading(true);
                                setIsLoading(false);
                            }}
                            disableLayout
                            enablePrintMode
                        />
                    </>
                )}
            </>
        );
    };

    if (
        isLoading ||
        !reduxStore.assignmentProgressDataLoaded ||
        !reduxStore.deploymentDataLoaded ||
        metricspawnQuery.isLoading
    ) {
        return <Loader loading />;
    }

    return <>{render()}</>;
};

PrivateSchoolDataEntry.propTypes = {
    defaultProp: PropTypes.string,
};

export default PrivateSchoolDataEntry;
